import {
  CheckOutlineIcon,
  CopyOutlineIcon,
  ShareOutlineIcon,
} from '@ikyucom/design-system-icons-react'
import * as Popover from '@radix-ui/react-popover'
import * as Tooltip from '@radix-ui/react-tooltip'
import { useCallback, useState } from 'react'
import type { ComponentType } from 'react'
import { css, sva } from '~/styled-system/css'
import { hstack, vstack } from '~/styled-system/patterns'
import type { SystemStyleObject } from '~/styled-system/types'
import { IconButton } from '~/ui/IconButton'

const styles = sva({
  slots: ['popover', 'content', 'information', 'text'],
  base: {
    popover: vstack.raw({
      gap: '2',
      alignItems: 'start',
    }),
    content: {
      flex: '1',
      bg: 'white',
      shadow: 'outline.lg',
      rounded: 'md',
      p: '3',
      maxW: '[480px]',
    },
    information: hstack.raw({
      gap: '3',
      alignItems: 'end',
      border: 'light',
      p: '2',
    }),
    text: vstack.raw({
      gap: '1',
      alignItems: 'start',
      w: 'full',
    }),
  },
})

export function PcShareButton(props: {
  type: 'プラン' | 'レストラン'
  title: string
  description: string
  url: string
}) {
  const copy = useCopy(
    `${props.title}\n${props.description}\n${props.url}`,
    `${props.type}情報をコピー`
  )
  const ariaLabel = `この${props.type}情報を共有する`

  const classes = styles()
  return (
    <Popover.Root onOpenChange={copy.reset}>
      <Popover.Trigger asChild>
        <IconButton
          Icon={ShareOutlineIcon}
          theme="filled"
          size="md"
          css={css.raw({ flex: '1' })}
          aria-label={ariaLabel}
        />
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content align="end" alignOffset={-60} className={classes.content}>
          <Popover.Arrow width={12} height={6} className={css({ fill: 'white' })} />
          <div className={classes.popover}>
            <p className={css({ color: 'gray.700' })}>この{props.type}情報を送る</p>
            <div className={classes.information}>
              <p className={classes.text}>
                <span className={css({ fontWeight: 'bold' })}>{props.title}</span>
                <span>{props.description}</span>
                <span>{props.url}</span>
              </p>
              {copy.button}
            </div>
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  )
}

function useCopy(text: string, tooltipText: string) {
  const [open, setOpen] = useState(false)
  const [state, setState] = useState<{
    text: string
    Icon?: ComponentType<{ className?: string }>
    iconCss?: SystemStyleObject
  }>({ text: tooltipText })
  const onClick = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(text)
      setState({
        text: 'コピーしました',
        Icon: CheckOutlineIcon,
        iconCss: css.raw({ color: 'not.recommended.ikyu.green' }),
      })
    } catch {
      setState({ text: 'コピーできませんでした' })
    } finally {
      // 1000ms で初期状態に戻す
      setTimeout(() => setState({ text: tooltipText }), 1000)
    }
  }, [text, tooltipText])
  const reset = useCallback(() => setState({ text: tooltipText }), [tooltipText])

  return {
    button: (
      <Tooltip.Provider>
        <Tooltip.Root open={open} delayDuration={0}>
          <Tooltip.Trigger asChild>
            <IconButton
              theme="filled"
              size="sm"
              Icon={state.Icon ?? CopyOutlineIcon}
              onClick={onClick}
              onMouseEnter={() => setOpen(true)}
              onMouseLeave={() => setOpen(false)}
              iconCss={state.iconCss}
            />
          </Tooltip.Trigger>
          <Tooltip.Content className={css({ rounded: 'md', bg: 'black/60', p: '1' })}>
            <span className={css({ color: 'white', fontSize: 'sm' })}>{state.text}</span>
          </Tooltip.Content>
        </Tooltip.Root>
      </Tooltip.Provider>
    ),
    reset,
  }
}
